import {
  abs,
  clamp,
  Discard,
  dot,
  exp,
  Fn,
  float,
  fract,
  fwidth,
  If,
  length,
  max,
  min,
  mix,
  mx_noise_float,
  normalWorld,
  positionWorld,
  smoothstep,
  uniform,
  vec3,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { HollowDef } from '../../universe/HollowDef';
import type { TunnelMouth } from '../../universe/Tunnel';

const vec3Uniform = () => uniform(new THREE.Vector3());
type Vec3Uniform = ReturnType<typeof vec3Uniform>;

/**
 * Per-hollow uniforms. `centerRel` is the hollow centre relative to the camera,
 * refreshed every frame from f64 universe coordinates.
 */
export interface HollowGpu {
  def: HollowDef;
  centerRel: Vec3Uniform;
}

export function makeHollowGpu(def: HollowDef): HollowGpu {
  return { def, centerRel: vec3Uniform() };
}

/** Anti-aliased 3D grid lines on a curved surface, faded with distance. */
const gridLines = (p: any, n: any, spacing: number, width: number, fadeStart: number, fadeEnd: number) => {
  const q = p.div(spacing);
  const d = abs(fract(q.sub(0.5)).sub(0.5)).mul(spacing); // distance to nearest line (m), per axis
  const fw = max(fwidth(p), vec3(1e-4));
  const lines = vec3(1).sub(smoothstep(vec3(width).sub(fw), vec3(width).add(fw), d));
  // A plane x = k meets the surface at a line only where the normal isn't parallel to x.
  const weight = vec3(1).sub(n.mul(n));
  const l = max(max(lines.x.mul(weight.x), lines.y.mul(weight.y)), lines.z.mul(weight.z));
  const dist = length(positionWorld);
  return l.mul(float(1).sub(smoothstep(fadeStart, fadeEnd, dist)));
};

/** Wall of a smooth sandbox hollow: soft "continents", grid lines, exact mouth holes. */
export function createWallMaterial(h: HollowGpu, mouths: TunnelMouth[], lightsNode: THREE.LightsNode) {
  const mat = new THREE.MeshStandardNodeMaterial({ roughness: 0.92, metalness: 0 });
  mat.lightsNode = lightsNode;
  const mouthU = mouths.map((m) => ({
    point: uniform(m.point.clone().sub(h.def.center)),
    dir: uniform(m.dir.clone()),
    radius: m.radius,
  }));

  mat.colorNode = Fn(() => {
    const local = positionWorld.sub(h.centerRel).toVar();
    for (const m of mouthU) {
      const w = local.sub(m.point);
      const t = dot(w, m.dir);
      const perp = length(w.sub(m.dir.mul(t)));
      If(
        abs(t)
          .lessThan(30)
          .and(perp.lessThan(m.radius - 0.02)),
        () => {
          Discard();
        },
      );
    }
    const dir = local.normalize();
    const n1 = mx_noise_float(dir.mul(3.1).add(h.def.seed * 0.013));
    const n2 = mx_noise_float(dir.mul(11.0).add(7.3));
    const n3 = mx_noise_float(local.mul(0.02));
    const land = smoothstep(-0.05, 0.12, n1.add(n2.mul(0.25)));
    const meadow = mix(vec3(0.2, 0.36, 0.13), vec3(0.36, 0.47, 0.18), n3.mul(0.5).add(0.5));
    const sand = vec3(0.66, 0.58, 0.4);
    const lake = vec3(0.12, 0.3, 0.42);
    const shore = smoothstep(0.0, 0.06, n1.add(n2.mul(0.25)).sub(0.02));
    const base = mix(lake, mix(sand, meadow, shore), land);
    const n = normalWorld;
    const g1 = gridLines(local, n, 10, 0.06, 120, 500);
    const g2 = gridLines(local, n, 100, 0.35, 1200, 5000);
    const line = max(g1.mul(0.35), g2.mul(0.55));
    return mix(base, vec3(0.93, 0.88, 0.72), line);
  })();
  return mat;
}

/**
 * Tunnel rock. Lit only by the lantern (and whatever lights are passed); sunlight
 * leaks in near each mouth and fades with depth into the rock. Fragments inside a
 * hollow are discarded so the tube is cut exactly at the wall.
 */
export function createTunnelMaterial(hollows: HollowGpu[], lightsNode: THREE.LightsNode, leak: THREE.Color) {
  const mat = new THREE.MeshStandardNodeMaterial({ roughness: 0.95, metalness: 0 });
  mat.lightsNode = lightsNode;
  const leakColor = uniform(leak);
  const depth = Fn(() => {
    let d: any = float(1e9);
    for (const h of hollows) {
      const r = length(positionWorld.sub(h.centerRel));
      If(r.lessThan(h.def.radius - 0.03), () => {
        Discard();
      });
      d = min(d, r.sub(h.def.radius));
    }
    return max(d, 0);
  });
  const albedo = Fn(() => {
    const p = positionWorld.sub(hollows[0].centerRel);
    const n1 = mx_noise_float(p.mul(0.35));
    const n2 = mx_noise_float(p.mul(1.7));
    const rock = mix(vec3(0.3, 0.26, 0.22), vec3(0.45, 0.4, 0.33), n1.mul(0.5).add(0.5));
    const g = gridLines(p, normalWorld, 2, 0.03, 20, 60);
    return mix(rock.mul(n2.mul(0.15).add(0.9)), vec3(0.75, 0.62, 0.42), g.mul(0.35));
  });
  const d = depth();
  const a = albedo();
  mat.colorNode = a;
  // Light spilling in from the hollows, fading over ~8 m of rock.
  mat.emissiveNode = a
    .mul(leakColor)
    .mul(exp(d.div(-8)))
    .mul(0.35);
  return mat;
}

/** Emissive sun body; HDR colour so it blooms and dominates exposure. */
export function createSunMaterial(color: THREE.Color, intensity: number) {
  const mat = new THREE.MeshBasicNodeMaterial();
  const c = uniform(color.clone().multiplyScalar(intensity));
  const rim = Fn(() => {
    // Brighter at the limb for a subtle corona feel.
    const v = clamp(dot(normalWorld, positionWorld.normalize().negate()), 0, 1);
    return c.mul(mix(float(1.6), float(1), v));
  });
  mat.colorNode = rim();
  return mat;
}
